import { memo } from 'react'
import { formatPersonName } from '../../utils/personUtils'
import { formatAbbreviatedNumber } from '../../utils/numberUtils'
import { PersonLiteDto } from '../../api/person/models/PersonDto'
import { MapExtra } from '../../utils/MapExtra'
import { NoteType } from '../../enums/NoteType'
import HelixSearchPersonFilterTag from '../../components/helixSearch/HelixSearchPersonFilterTag'
import Note from '../common/note/Note'
import Button from '../common/form/inputs/Button'
import Dropdown, { Item as DropdownItem } from '../common/form/inputs/DropDown'
import LoadingSpinner from '../common/loading/LoadingSpinner'
import MultiSelectDropdown from '../../components/MultiSelectDropdown'
import ToggleSwitch3 from '../../components/ToggleSwitch3'

const clampRelationshipStrength = (value: number) =>
    Math.min(Math.max(Math.round(value / 10) * 10, 0), 100)

const relationshipStrengthValueFromId = (scoreId: string): number => {
    if (scoreId === 'min_relationship_score_any') {
        return 0
    }

    const parsed = parseInt(scoreId.split('_').pop() ?? '0', 10)

    if (Number.isNaN(parsed)) {
        return 0
    }

    return clampRelationshipStrength(parsed)
}

const relationshipStrengthIdFromValue = (value: number): string => {
    const normalizedValue = clampRelationshipStrength(value)

    return normalizedValue === 0
        ? 'min_relationship_score_any'
        : `min_relationship_score_${normalizedValue}`
}

interface Props {
    roleId: string | null
    enableLlmScoring: boolean
    personLiteCache: MapExtra<string, PersonLiteDto>
    availableNetworkLinkIds: string[]
    applyClick: () => void
    resetClick: () => void
    networkLinksSelected: string[]
    shortlistedSelected: string[]
    minRelationshipScore: string
    orderBy: string
    filteredCandidatePoolCount: number
    filteredCandidatePoolCountIsLoading: boolean
    filtersAreDirty: boolean
    networkLinksSelectedChanged: (selectedIds: string[]) => void
    shortlistedSelectedChanged: (selectedIds: string[]) => void
    minRelationshipScoreChanged: (selectedId: string) => void
    orderByChanged: (selectedId: string) => void
    mustBeInBooleanSearchResultsChanged: (value: boolean) => void
    mustHaveRelationshipChanged: (value: boolean) => void
}

// TODO: 77 - when filters change we should call /get-helix-search-results-count-only to update the candidate pool count
// TODO: 77 - hide the shortlist filter if we're not in the context of a role
// TODO: 77 - need an "isDirty" flag to enable/disable the apply button

function HelixSearchV2Filters({
    roleId,
    enableLlmScoring,
    personLiteCache,
    availableNetworkLinkIds,
    applyClick,
    resetClick,
    networkLinksSelected,
    shortlistedSelected,
    minRelationshipScore,
    orderBy,
    filteredCandidatePoolCount,
    filteredCandidatePoolCountIsLoading,
    filtersAreDirty,
    networkLinksSelectedChanged,
    shortlistedSelectedChanged,
    minRelationshipScoreChanged,
    orderByChanged,
}: Props) {
    const orderByItems: DropdownItem[] = [
        {
            id: 'combined_desc',
            content: 'Combined (highest first)',
        },
        {
            id: 'combined_asc',
            content: 'Combined (lowest first)',
        },
        {
            id: 'booleanSearchScore_desc',
            content: 'Job Relevance (highest first)',
        },
        {
            id: 'booleanSearchScore_asc',
            content: 'Job Relevance (lowest first)',
        },
        {
            id: 'relationshipScore_desc',
            content: 'Network Strength (highest first)',
        },
        {
            id: 'relationshipScore_asc',
            content: 'Network Strength (lowest first)',
        },
    ]

    // ✅ Add LLM scoring options only if enabled
    if (enableLlmScoring) {
        orderByItems.push(
            {
                id: 'llmRelevanceScore_desc',
                content: '✨ Contxt Score (highest first)',
            },
            {
                id: 'llmRelevanceScore_asc',
                content: '✨ Contxt Score (lowest first)',
            },
        )
    }

    const selectedOrderByItem =
        orderByItems.find((item) => item.id === orderBy) ?? orderByItems[0]

    const relationshipStrengthValue =
        relationshipStrengthValueFromId(minRelationshipScore)
    const relationshipStrengthLabel =
        relationshipStrengthValue === 0
            ? 'Any'
            : relationshipStrengthValue === 100
              ? 'Strongest'
              : `${relationshipStrengthValue}+`

    const showSavedCandidates = shortlistedSelected.includes('yes')
    const showHiddenCandidates = shortlistedSelected.includes('no')

    const buildShortlistedSelection = (
        saved: boolean,
        hidden: boolean,
    ): string[] => {
        const includeBlanks =
            shortlistedSelected.includes('blanks') || (!saved && !hidden)
        const nextSelection: string[] = includeBlanks ? ['blanks'] : []

        if (saved) {
            nextSelection.push('yes')
        }
        if (hidden) {
            nextSelection.push('no')
        }

        return nextSelection
    }

    return (
        <div>
            <div className="mt-4">
                <div>
                    <MultiSelectDropdown
                        label="Network Links"
                        onSelectionChange={networkLinksSelectedChanged}
                        selectedItemIds={networkLinksSelected}
                        items={availableNetworkLinkIds
                            .slice() // make a shallow copy to avoid mutating original array
                            .sort((a, b) => {
                                const personA = personLiteCache.getOrThrow(a)
                                const personB = personLiteCache.getOrThrow(b)

                                const fullNameA =
                                    formatPersonName(personA).toLowerCase()
                                const fullNameB =
                                    formatPersonName(personB).toLowerCase()

                                return fullNameA.localeCompare(fullNameB)
                            })
                            .map((personId) => {
                                const person =
                                    personLiteCache.getOrThrow(personId)

                                return {
                                    id: person.id,
                                    value: (
                                        <HelixSearchPersonFilterTag
                                            personLite={person}
                                        />
                                    ),
                                }
                            })}
                    />
                </div>
                <div className="flex gap-4 mt-4 items-start">
                    {roleId && (
                        <div className="flex flex-col gap-2 border border-gray-200 rounded-lg px-3 py-2 min-w-[230px] ">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm text-gray-900">
                                    Show Saved Candidates
                                </span>
                                <ToggleSwitch3
                                    value={showSavedCandidates}
                                    onChange={(checked) =>
                                        shortlistedSelectedChanged(
                                            buildShortlistedSelection(
                                                checked,
                                                showHiddenCandidates,
                                            ),
                                        )
                                    }
                                />
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm text-gray-900">
                                    Show Hidden Candidates
                                </span>
                                <ToggleSwitch3
                                    value={showHiddenCandidates}
                                    onChange={(checked) =>
                                        shortlistedSelectedChanged(
                                            buildShortlistedSelection(
                                                showSavedCandidates,
                                                checked,
                                            ),
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col gap-2 min-w-[260px]">
                        <label
                            className="text-sm text-gray-900 font-bold"
                            htmlFor="relationship-strength"
                        >
                            Relationship Strength
                        </label>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-600 text-right">
                                Weak
                            </span>
                            <input
                                id="relationship-strength"
                                type="range"
                                min={0}
                                max={100}
                                step={10}
                                value={relationshipStrengthValue}
                                aria-valuetext={relationshipStrengthLabel}
                                onChange={(event) =>
                                    minRelationshipScoreChanged(
                                        relationshipStrengthIdFromValue(
                                            Number(event.target.value),
                                        ),
                                    )
                                }
                                className="flex-1 accent-ctxt-blue"
                            />
                            <span className="text-xs text-gray-600">
                                Strong
                            </span>
                        </div>
                    </div>
                    <Dropdown
                        id="order-by"
                        name="Order By"
                        placeholder="Select sort order"
                        items={orderByItems}
                        selected={selectedOrderByItem}
                        onChange={(item) => orderByChanged(item.id)}
                    />
                </div>
            </div>
            <div className="flex items-center justify-between mt-4">
                <div className="text-right">
                    <div className="text-sm text-gray-900">
                        Filtered Candidates
                    </div>
                    {filteredCandidatePoolCountIsLoading && (
                        <div className="text-right">
                            <LoadingSpinner spinnerSize="28px" />
                        </div>
                    )}
                    {!filteredCandidatePoolCountIsLoading && (
                        <div
                            className={`text-xl font-mono tracking-wide font-bold ${
                                filteredCandidatePoolCount > 0
                                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-ctxt-blue-dark via-ctxt-blue to-sky-500'
                                    : 'text-red-400'
                            }`}
                        >
                            {formatAbbreviatedNumber(
                                filteredCandidatePoolCount,
                            )}
                        </div>
                    )}
                </div>
                <div className="">
                    <div className="flex gap-2 justify-end">
                        <Button
                            type="button"
                            style="mild-danger"
                            onClick={resetClick}
                            size="small"
                        >
                            Reset Filters
                        </Button>
                        <Button
                            type="button"
                            style="primary"
                            onClick={applyClick}
                            size="small"
                            enabled={filtersAreDirty}
                        >
                            Apply Filters
                        </Button>
                    </div>
                    <div>
                        {filtersAreDirty && (
                            <Note type={NoteType.WARNING}>
                                Apply filters to see updated candidate results
                            </Note>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default memo(HelixSearchV2Filters)
